import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  Field,
  FieldLabel,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Text,
  Textarea,
} from '@electricjs/arc';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useSendISVRequestMutation } from '@/redux/slices/requestApiSlice';

const MessageInput = styled(Textarea)`
  height: 20rem !important;
`;

type SendAddonEmailModalProps = {
  companyName: string;
  vendorName: string;
  vendorEmail: string;
  userEmail: string;
  closeModalAction: () => void;
};

const SendAddonEmailModal = ({
  companyName,
  vendorName,
  vendorEmail,
  userEmail,
  closeModalAction,
}: SendAddonEmailModalProps) => {
  const { t } = useTranslation('support-center');
  const [message, setMessage] = useState('');

  const { showToast } = useGlobalUI();

  const [sendISVRequestEmail, { isLoading }] = useSendISVRequestMutation();

  const organizationId = useGetOrganizationId();

  const handleSendAddonRequestAction = () => {
    const maybeEmployee = userEmail
      ? `This is a request from ${userEmail}`
      : `This is a request regarding ${vendorName}`;
    const connector = userEmail ? 'of' : 'by';
    const maybeCompany = companyName ? ` ${connector} ${companyName}` : '';
    const heading = `${maybeEmployee}${maybeCompany}`;

    sendISVRequestEmail({
      organizationId,
      heading,
      message,
      vendorEmail,
      vendorName,
    })
      .then(() => {
        showToast({
          id: 'send-isv-request-success-toast',
          intent: 'success',
          title: t('modal.successToastTitle'),
          message: t('modal.successToastMessage', { vendorName }),
        });
        closeModalAction();
      })
      .catch(() => {
        showToast({
          id: 'send-isv-request-error-toast',
          intent: 'error',
          title: t('modal.errorToastTitle'),
          message: t('modal.errorToastMessage'),
        });
      });
  };

  return (
    <ModalV2 ariaLabelledby="ISV Request" visible hide={closeModalAction}>
      <ModalHeader>
        <Text variant="heading-2" id="ISV Request">
          {t('modal.title', { vendorName })}
        </Text>
      </ModalHeader>
      <ModalBody minHeight="17rem">
        <Text>{t('modal.description')}</Text>
        <Field margin="2rem 0" width="100%">
          <FieldLabel htmlFor="message">{t('modal.requestLabel')}</FieldLabel>
          <MessageInput
            id="message"
            placeholder={t('modal.placeholder')}
            onChange={e => {
              setMessage(e.target?.value);
            }}
            resize={false}
          />
        </Field>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction
            id="send-addon-email-cancel-button"
            variant="outline"
            onClick={closeModalAction}>
            {t('modal.cancel')}
          </ModalAction>
          <ModalAction
            id="send-addon-email-submit-button"
            onClick={handleSendAddonRequestAction}
            disabled={!message}
            loading={isLoading}>
            {t('modal.submit')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default SendAddonEmailModal;
